import { Event, IPartnerProductSearchResult, ISolutionArticleSearchResult } from "@iloq/shared";
import { IMaterial } from "@iloq/shared";
import {
  ISearchable,
  ISearchResults,
  ISearchResult,
  ISearchResultAttachment,
  LocallySearchableAsset,
} from "@iloq/shared";
import i18n from "../i18n";
import { Dispatch } from "vuex";

const stripHtmlTags = (html: string) => html.replace(/(<([^>]+)>)/gi, "");

const wpDoc2SearchableMapper = (wpDoc: {
  wpDocType:
    | LocallySearchableAsset.news
    | LocallySearchableAsset.blogpost
    | LocallySearchableAsset.bulletin;
  id: number;
  title: string;
  content: string;
  link: string;
  date: string;
}): ISearchable => {
  const titleStr = stripHtmlTags(wpDoc.title);
  const contentStr = stripHtmlTags(wpDoc.content);

  // Default values for news
  let iconName = "news";
  let externalUrl: string | undefined = wpDoc.link;
  let path: string | undefined;
  if (wpDoc.wpDocType === LocallySearchableAsset.blogpost) {
    iconName = "blog";
  } else if (wpDoc.wpDocType === LocallySearchableAsset.bulletin) {
    iconName = "info";
    externalUrl = undefined;
    path = `/bulletin/${wpDoc.id}`;
  }

  return {
    searchableData: [
      {
        content: titleStr,
        weight: 3,
      },
      {
        content: contentStr,
        weight: 1,
      },
    ],
    result: {
      title: titleStr,
      iconName,
      tag: i18n.t(`globalSearch.tag.${wpDoc.wpDocType}`).toString(),
      path,
      externalUrl,
    },
  };
};

export const searchableMappers: {
  [key in LocallySearchableAsset]: (data: any[], dispatch: Dispatch) => ISearchable[];
} = {
  [LocallySearchableAsset.event]: (events: Event[]) =>
    events.map((event) => ({
      searchableData: [
        {
          content: event.name,
          weight: 3,
        },
        {
          content: `${event.location} ${event.address} ${event.city}`,
          weight: 1,
        },
      ],
      result: {
        title: event.name,
        iconName: "calendar_learning",
        externalUrl: event.enrollmentUrl,
      },
    })),
  [LocallySearchableAsset.news]: (docs: any[]) => docs.map(wpDoc2SearchableMapper),
  [LocallySearchableAsset.blogpost]: (docs: any[]) => docs.map(wpDoc2SearchableMapper),
  [LocallySearchableAsset.bulletin]: (docs: any[]) => docs.map(wpDoc2SearchableMapper),
  [LocallySearchableAsset.material]: (material: IMaterial[], dispatch) =>
    material.map((m) => ({
      searchableData: [
        {
          content: m.name || "",
          weight: 3,
        },
        {
          content: m.AssetName || "",
          weight: 1,
        },
        {
          content: m.Description || "",
          weight: 1,
        },
        {
          content: m.Categories.map((category) =>
            i18n.t(`material.category.${category.id}`).toString()
          ).join(" "),
          weight: 1,
        },
      ],
      result: {
        title: m.name,
        iconName: "marketing",
        clickHandler() {
          dispatch("material/downloadMaterial", m, { root: true });
        },
      },
    })),
};

export const searchResultMappers = {
  product: (
    result: IPartnerProductSearchResult,
    requestedCount: number,
    smallListLimit: number
  ): ISearchResults => ({
    count: result.count,
    requestedCount,
    smallListLimit,
    data: result.value.map(
      (product): ISearchResult => {
        const attachments: ISearchResultAttachment[] = [];
        const datasheet = product.attachments?.find((a) => a.id === "datasheet");
        const installationInstructions = product.attachments?.find(
          (a) => a.id === "installation_instructions"
        );
        if (datasheet) {
          attachments.push({
            title: i18n.t("globalSearch.attachmentTitle.datasheet").toString(),
            iconName: "datasheet",
            url: datasheet.url,
          });
        }
        if (installationInstructions) {
          attachments.push({
            title: i18n.t("globalSearch.attachmentTitle.installationInstructions").toString(),
            iconName: "wrench",
            url: installationInstructions.url,
          });
        }
        return {
          imageAddress: product.main_image,
          title: product.product_name,
          subTitle: product.sku,
          attachments,
          path: `/products/${product.sku}`,
        };
      }
    ),
  }),
  supportArticle: (
    result: ISolutionArticleSearchResult,
    requestedCount: number,
    smallListLimit: number
  ): ISearchResults => ({
    count: result.count,
    requestedCount,
    smallListLimit,
    data: result.value.map((article) => ({
      title: article.title,
      iconName: "support_doc",
      tag: article.categoryName,
      path: `/support/solutions/categories/${article.categoryId}/${article.folderId}/${article.freshdeskId}`,
    })),
  }),
};
